import { useState } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import type { components } from '@/api/schema'
import { useMutation, useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type Conflict = components['schemas']['CannibalizationConflictOut']
type ConflictPage = components['schemas']['ConflictPageOut']

/**
 * Which **kind** of conflict is on screen — S-D5's segmented control.
 *
 * **A kind, not a site, and that is a correction (SEO-UI-20).** The control used
 * to send `?site_id=` for *Within a site*, taking the first site of the list
 * because nothing on the screen asks which — so a tenant with three sites had a
 * button labelled *Within a site* that meant *within whichever site the API
 * happened to return first*, with no way to reach the other two. It did not even
 * exclude the thing its opposite is named for: the store deliberately widens a
 * `site_id` filter to every row of any group that touches that site, so
 * cross-site pairs came back under *Within a site* as well.
 *
 * The two labels are categories — the alert above the list counts in exactly
 * these words — and the difference between them is what the whole screen turns
 * on, because a redirect answers one and can never answer the other. So the
 * filter is `cross_site`, applied here: `GET /seo/cannibalization` already
 * answers with every conflict of the tenant in one response, so this is a
 * predicate over an array that is already in the browser rather than a round
 * trip to re-ask a question the answer to which is in hand.
 */
type Scope = 'within' | 'across'

/** Which plan a tenant recorded, from `CONFLICT_DECISIONS` in `db/seo_rank.py`. */
type Decision = 'merge_redirect' | 'differentiate' | 'split_intent' | 'concede'

/** One button in the card footer: what it records and what it says afterwards. */
type Plan = {
  decision: Decision
  label: string
  done: string
  /** The sentence a decided card carries, in the tenant's own past tense. */
  chosen: string
}

/**
 * Cannibalization — S-D5's `seo#cannib`, on real conflicts (S-B24).
 *
 * The design's own note is the whole argument for this shape: cannibalization is
 * **invisible to most site owners** — two of your own pages fighting over one
 * query — so a card shows the pair, the query, and the recommended action,
 * because the fix is a judgement call that must not be made silently.
 *
 * **Nothing here is applied.** A 301 is a rule in somebody's webserver or CDN and
 * no publisher in `app/seo/` writes one; retargeting a page at a different term
 * is a brief, which is the content pipeline. What this screen records is the
 * tenant's judgement, so the next run stops raising the pair as new and the card
 * stops asking. Saying that out loud in the copy is the difference between an
 * honest screen and one that implies work AugRank has not done — which is why
 * every button here is in the first person and none of them is an instruction.
 *
 * **Two plans per card, not one (SEO-UI-17).** S-D5 draws the recommendation and
 * the alternative to it side by side, and they are genuinely different answers: a
 * tenant who reads *merge these two pages* and decides to keep both instead has
 * disagreed, and recording that as a bare *acknowledged* filed the disagreement
 * as agreement. `decision` is the field that carries which, and a decided card
 * reads it back.
 *
 * **The cross-site case gets different buttons and it is not cosmetic.** S-D5:
 * *"you cannot 301 between two domains you sell different things from"*. The
 * server never recommends `merge_redirect` for a cross-site conflict, this screen
 * never offers it, and the decision route now refuses to record it — three
 * independent statements of one rule, which is what makes it survive an edit to
 * any of them.
 */
export function Cannibalization(): JSX.Element {
  const brand = useBrand()
  const toast = useToast()
  // SEO-UI-20. S-D5 draws *Within a site* pressed, and it is the right default
  // as well as the drawn one: two pages of one site fighting each other is the
  // case every tenant can have, and the cross-site case needs a second site
  // before it exists at all.
  const [scope, setScope] = useState<Scope>('within')

  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])
  const listed = sites.state.data?.sites ?? []

  const conflicts = useQuery(
    (signal) => call(() => api.GET('/api/v1/seo/cannibalization', { signal })),
    [],
  )

  const decide = useMutation(
    (input: { group: string; status: string; decision: Decision | null }) =>
      call(() =>
        api.POST('/api/v1/seo/cannibalization/{conflict_group}', {
          params: { path: { conflict_group: input.group } },
          body: { status: input.status, decision: input.decision },
        }),
      ),
  )

  const found = conflicts.state.data?.conflicts ?? []
  const crossSite = found.filter((row) => row.cross_site).length
  const within = found.length - crossSite

  /**
   * Whether more than one site is in play — and it is **not** read from the
   * sites request alone, which is the defect this line exists to close.
   *
   * Found reverifying. `GET /seo/sites` and `GET /seo/cannibalization` are two
   * requests, and only the second is needed to draw this screen. When the first
   * failed and the second did not, `listed` was empty, the segmented control
   * disappeared with it, and the default *within* filter then hid every
   * cross-site pair — underneath an alert still counting them out loud. The
   * empty-state guard below could not catch it either: it fires when *this*
   * bucket is empty, and it was not.
   *
   * The conflicts carry the answer themselves. A page knows its `site_id`, so
   * two distinct ones across the set is proof of a second site that no second
   * request can take away — and a cross-site pair is two by construction. The
   * sites list stays in the union for the case it is the only witness: a tenant
   * with three sites whose conflicts all happen to sit on one of them.
   */
  const involved = new Set(found.flatMap((row) => row.pages.map((page) => page.site_id)))
  const multiSite = listed.length > 1 || involved.size > 1
  const showing = found.filter((row) => (scope === 'across' ? row.cross_site : !row.cross_site))
  // The other bucket's count, for the empty state that has to say where the
  // conflicts went rather than claiming there are none.
  const elsewhere = found.length - showing.length

  function record(group: string, status: string, decision: Decision | null, done: string): void {
    void decide.run({ group, status, decision }).then((result) => {
      if (!result.ok) return
      conflicts.refetch()
      toast.show({ kind: 'success', title: done })
    })
  }

  return (
    <>
      <div className="ar-row">
        <h1 className="ar-h1">Keyword cannibalization</h1>
        <span className="app-spacer" />
        {/* Always drawn (parity P-CAN-01): *Across your sites* is a question a
            one-site tenant can ask and get the honest empty answer to. */}
        <div className="app-seg" role="group" aria-label="Which pairs">
          <button
            type="button"
            aria-pressed={scope === 'within'}
            onClick={() => {
              setScope('within')
            }}
          >
            Within a site
          </button>
          <button
            type="button"
            aria-pressed={scope === 'across'}
            onClick={() => {
              setScope('across')
            }}
          >
            Across your sites
          </button>
        </div>
        {/* SEO-UI-57. Not drawn, and kept: this screen is reached from one link
            on the keywords tab strip and has no other way back to it. */}
        <Link className="ar-link" to="/seo/keywords">
          Back to keywords
        </Link>
      </div>

      <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
        Two of your pages competing for one search. Google picks between them, and neither ranks as
        well as one strong page would. {brand.productName} finds these by comparing what your pages
        actually say — nothing is changed until you decide.{' '}
        {/* SEO-UI-56. The window, said once for the screen rather than
            abbreviated into a column heading. `seo.html:456` labels the figure
            *Clicks/mo* and a month is not what any of these are: the rank run
            reads `gsc.DEFAULT_WINDOW_DAYS` — 28 days — ending `DATA_LAG_DAYS`
            back, because Google's most recent days are incomplete and would
            change under the tenant. The same sentence the tracked table carries
            under its own figures. */}
        The figures are what Search Console reports over 28 days, ending three days back.
      </p>

      {decide.state.status === 'error' ? <InlineError failure={decide.state.failure} /> : null}

      {conflicts.state.status === 'loading' ? (
        <Loading label="Loading conflicts" />
      ) : conflicts.state.status === 'error' ? (
        <ErrorState failure={conflicts.state.failure} onRetry={conflicts.refetch} />
      ) : found.length === 0 ? (
        <Empty
          title="No pages are competing with each other"
          body={`Either nothing is, or ${brand.productName} has not read your rankings yet. Refreshing from the keywords screen checks both.`}
          action={<Link to="/seo/keywords">Go to keywords</Link>}
        />
      ) : (
        <div className="ar-stack">
          <div className="ar-alert ar-alert--warning">
            <span className="ar-alert__icon" aria-hidden="true">
              ⚠
            </span>
            <div className="ar-alert__body">
              <strong>{summarise(within, crossSite)}</strong>
              Google is picking between them and neither ranks as well as one strong page would.
            </div>
          </div>
          {/* The filter must never be able to report *nothing found* while the
              alert above it is counting pairs. When this bucket is empty and the
              other is not, the screen says so and offers the press. */}
          {showing.length === 0 ? (
            <Empty
              icon="⌕"
              title={
                scope === 'within'
                  ? 'No two pages of one site are competing'
                  : 'No pages are competing across your sites'
              }
              body={
                scope === 'within'
                  ? `Every conflict ${brand.productName} found is between two different sites of yours — ${String(elsewhere)} of them.`
                  : `Every conflict ${brand.productName} found is between two pages of one site — ${String(elsewhere)} of them.`
              }
              action={
                <Button
                  tone="primary"
                  onClick={() => {
                    setScope(scope === 'within' ? 'across' : 'within')
                  }}
                >
                  Show those instead
                </Button>
              }
            />
          ) : (
            showing.map((conflict) => (
              <ConflictCard
                key={conflict.conflict_group}
                conflict={conflict}
                multiSite={multiSite}
                pending={decide.pending}
                onRecord={record}
              />
            ))
          )}
        </div>
      )}
    </>
  )
}

function summarise(within: number, crossSite: number): string {
  const parts: string[] = []
  if (within > 0) parts.push(`${String(within)} pair${within === 1 ? '' : 's'} within a site`)
  if (crossSite > 0) {
    parts.push(`${String(crossSite)} pair${crossSite === 1 ? '' : 's'} across your sites`)
  }
  return parts.join(', ')
}

/**
 * The two plans this card offers, and which of them AugRank recommends.
 *
 * **The alternative is derived from the recommendation rather than fixed**, for
 * the reason S-D5 draws two different footers: within one site the choice is
 * merge-or-differentiate in whichever order the evidence puts them, and across
 * two sites a redirect is not on the table at all, so the pair there is the split
 * and conceding the query outright. Conceding is not the split with the retarget
 * dropped — it is giving up on the weaker page for this search and doing nothing
 * else, which is a real answer for a tenant who has no appetite for a second
 * content plan.
 */
function plans(conflict: Conflict): { primary: Plan; alternative: Plan } {
  const merge: Plan = {
    decision: 'merge_redirect',
    label: 'I’ll merge and redirect',
    done: 'Noted — you are merging these two pages.',
    chosen: 'merge the weaker page into the stronger one and redirect it',
  }
  const differentiate: Plan = {
    decision: 'differentiate',
    label: 'I’ll keep both and differentiate',
    done: 'Noted — you are keeping both pages.',
    chosen: 'keep both pages and make them about different things',
  }
  const split: Plan = {
    decision: 'split_intent',
    label: 'I’ll apply the split',
    done: 'Noted — you are splitting the intent between the two sites.',
    chosen: 'let one site own this search and retarget the other',
  }
  const concede: Plan = {
    decision: 'concede',
    label: 'I’ll concede the query',
    done: 'Noted — the weaker page is giving up this search.',
    chosen: 'let one site have this search and stop competing for it',
  }

  if (conflict.recommendation === 'merge_redirect') {
    return { primary: merge, alternative: differentiate }
  }
  if (conflict.recommendation === 'split_intent') {
    return { primary: split, alternative: concede }
  }
  // `differentiate` — neither page is clearly stronger, so the recommendation is
  // the tenant's own judgement and merging is the alternative to it. Never
  // reached with `cross_site`, which the detector always recommends splitting.
  return { primary: differentiate, alternative: merge }
}

/**
 * One conflict: the query, the pages, what AugRank would do, and the decision.
 *
 * The similarity is shown as a badge because it is the evidence — *"92% similar"*
 * is what makes the pair believable when a tenant looks at two URLs and thinks
 * they are different pages. The impact is shown in impressions rather than as a
 * predicted position gain, because nothing in this product can honestly produce
 * the second number.
 */
function ConflictCard({
  conflict,
  multiSite,
  pending,
  onRecord,
}: {
  conflict: Conflict
  multiSite: boolean
  pending: boolean
  onRecord: (group: string, status: string, decision: Decision | null, done: string) => void
}): JSX.Element {
  const decided = conflict.status !== 'open'
  const { primary, alternative } = plans(conflict)
  const taken = [primary, alternative].find((plan) => plan.decision === conflict.decision) ?? null

  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h2>&ldquo;{conflict.query}&rdquo;</h2>
        <span className="ar-card__spacer" />
        {conflict.cross_site ? (
          <span className="ar-badge ar-badge--warning">Across your sites</span>
        ) : null}
        {decided ? <span className="ar-badge">{statusLabel(conflict.status)}</span> : null}
        <span className="ar-badge ar-badge--danger">
          {(conflict.similarity * 100).toFixed(0)}% similar
        </span>
        {/* A model read both pages and agreed. The badge says the pair was
            checked; the sentence below says what each page is for, which is a
            better reason to merge than a percentage. Absent for a pair reported
            on the embedding alone. */}
        {conflict.confirmation !== null && conflict.confirmation !== undefined ? (
          <span className="ar-badge ar-badge--success">Read and confirmed</span>
        ) : null}
      </div>
      <div className="ar-card__body ar-stack">
        {conflict.confirmation !== null && conflict.confirmation !== undefined ? (
          <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
            {conflict.confirmation}
          </p>
        ) : null}
        <div className="ar-grid ar-grid--2">
          {conflict.pages.map((page) => (
            <PagePanel
              key={page.id}
              page={page}
              // SEO-UI-19, found reverifying. The domain used to appear only on
              // a cross-site card, which left a tenant with three sites reading
              // `/wardrobes` against `/wardrobes` with nothing saying whose. It
              // is the number of *sites* that decides whether a path is
              // ambiguous, not the kind of conflict.
              showDomain={conflict.cross_site || multiSite}
            />
          ))}
        </div>
        <div className="ar-panel">
          <strong>What we&rsquo;d do</strong>
          <p className="ar-text-body-sm ar-muted" style={{ margin: 'var(--ar-space-2) 0 0' }}>
            {advice(conflict)}
          </p>
          <p
            className="ar-muted"
            style={{ margin: 'var(--ar-space-2) 0 0', fontSize: 'var(--ar-text-caption-size)' }}
          >
            About {Math.round(conflict.impact).toLocaleString('en-IN')} of the impressions for this
            search are going to a page that is not the one you would want ranking. Nothing happens
            until you approve.
          </p>
          {/* SEO-UI-17. What the tenant decided, which is not always what
              AugRank recommended — the whole reason the footer has two buttons.
              Read back from `decision` so the card says it after a reload and
              after the next run re-detects the pair. */}
          {taken !== null ? (
            <p className="ar-text-body-sm" style={{ margin: 'var(--ar-space-3) 0 0' }}>
              You chose to <strong>{taken.chosen}</strong>.
            </p>
          ) : null}
        </div>
      </div>
      <div className="ar-card__footer">
        {/* SEO-UI-55. `seo.html:478-481` draws primary, ghost, spacer, quiet —
            three weights for three different things. Every one of these was a
            bare `<Button>`, which defaults to ghost, so the card's recommended
            plan rendered as the quietest control on it. */}
        <Button
          tone="primary"
          disabled={pending || conflict.decision === primary.decision}
          onClick={() => {
            onRecord(conflict.conflict_group, 'acknowledged', primary.decision, primary.done)
          }}
        >
          {primary.label}
        </Button>
        {/* Still pressable on a decided card, and deliberately: changing your
            mind about a plan nothing has carried out yet costs one press, and
            the alternative is the press that says so. */}
        <Button
          tone="ghost"
          disabled={pending || conflict.decision === alternative.decision}
          onClick={() => {
            onRecord(
              conflict.conflict_group,
              'acknowledged',
              alternative.decision,
              alternative.done,
            )
          }}
        >
          {alternative.label}
        </Button>
        <span className="ar-card__spacer" />
        <Button
          tone="quiet"
          disabled={pending}
          onClick={() => {
            // Reopening clears the plan as well as the status: the server drops
            // `decision` whenever one is not sent, so a pair put back on the
            // list carries no stale *you chose to merge these*.
            onRecord(
              conflict.conflict_group,
              decided ? 'open' : 'dismissed',
              null,
              decided ? 'Back on the list.' : 'Ignored.',
            )
          }}
        >
          {decided ? 'Put it back' : 'Ignore this pair'}
        </Button>
      </div>
    </section>
  )
}

/** One page of the pair, with the figures that decide which should win. */
function PagePanel({ page, showDomain }: { page: ConflictPage; showDomain: boolean }): JSX.Element {
  const brand = useBrand()
  return (
    <div className="ar-panel">
      <div className="ar-mono ar-text-body-sm">
        {showDomain ? `${page.domain}${pathOf(page.url)}` : pathOf(page.url)}
      </div>
      <div className="ar-row" style={{ marginTop: 'var(--ar-space-3)', gap: 'var(--ar-space-5)' }}>
        <Figure
          label="Position"
          value={
            // SEO-UI-19, one decimal — the same measurement the keywords table
            // shows, so the two screens stop disagreeing about one page. It is
            // Search Console's *average* over the window, and 14.0 and 14.4 are
            // different facts that `toFixed(0)` printed identically.
            page.position === null || page.position === undefined ? '—' : page.position.toFixed(1)
          }
        />
        <Figure label="Clicks" value={page.clicks.toLocaleString('en-IN')} />
        <Figure label="Impressions" value={page.impressions.toLocaleString('en-IN')} />
      </div>
      {page.primary ? (
        <p
          className="ar-muted"
          style={{ margin: 'var(--ar-space-2) 0 0', fontSize: 'var(--ar-text-caption-size)' }}
        >
          The stronger of the two on the evidence.
        </p>
      ) : null}
      {!page.read ? (
        <p
          className="ar-muted"
          style={{ margin: 'var(--ar-space-2) 0 0', fontSize: 'var(--ar-text-caption-size)' }}
        >
          {brand.productName} could not read this page, so the comparison used the other one only.
        </p>
      ) : null}
    </div>
  )
}

/**
 * The recommendation, in the tenant's terms rather than as a slug.
 *
 * Composed here rather than sent by the server because it is copy: the server
 * sends *which* of the three this is, and the sentence around it belongs with
 * the screen that renders it — which is the same split the audit screen makes
 * between a finding's `code` and its label.
 */
function advice(conflict: Conflict): string {
  const strongest = conflict.pages.find((page) => page.primary)
  const weakest = conflict.pages.find((page) => !page.primary)
  const strong = strongest === undefined ? 'the stronger page' : pathOf(strongest.url)
  const weak = weakest === undefined ? 'the other page' : pathOf(weakest.url)

  if (conflict.recommendation === 'merge_redirect') {
    return `Merge ${weak} into ${strong} — it has more clicks and a better position — then redirect the old address to it, so the strength of both lands on one page.`
  }
  if (conflict.recommendation === 'split_intent') {
    return `These are two of your own sites, so a redirect would throw away one of the brands. Let ${strong} own this search, and retarget ${weak} at a related term nobody in your set holds.`
  }
  return `Neither page is clearly stronger — one wins on clicks and the other on position — so this is your call. Either make ${weak} about something distinctly different, or fold it into ${strong}.`
}

function statusLabel(status: string): string {
  if (status === 'acknowledged') return 'Acknowledged'
  if (status === 'dismissed') return 'Ignored'
  if (status === 'resolved') return 'Resolved'
  return 'Open'
}

function Figure({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <div className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
        {label}
      </div>
      <strong className="ar-num">{value}</strong>
    </div>
  )
}

/** A URL as its path — the domain is on the card when it matters. */
function pathOf(url: string): string {
  try {
    const parsed = new URL(url)
    return `${parsed.pathname}${parsed.search}`
  } catch {
    return url
  }
}
